import type { Static, TSchema } from "@sinclair/typebox";
import type { ErrorCode } from "../errors";

export const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

/** One API module = one folder under apps/api/src/modules. */
export const API_MODULES = [
  "auth",
  "users",
  "personal",
  "catalog",
  "policy",
  "admin",
  "downloads",
  "reader",
  "internal",
  "videos",
  "bot",
] as const;
export type ApiModule = (typeof API_MODULES)[number];

/**
 * Who may call a route.
 *   public   no login
 *   user     any logged-in account (roles admin, user, guest)
 *   admin    role admin only
 *   internal HMAC-signed request from a Lambda (never a browser)
 */
export type RouteAccess = "public" | "user" | "admin" | "internal" | "discord";

export interface RouteSpec {
  module: ApiModule;
  method: HttpMethod;
  /** Elysia/Express style: `/api/galleries/:gid`. */
  path: string;
  access: RouteAccess;
  summary: string;
  params?: TSchema;
  query?: TSchema;
  body?: TSchema;
  /** status -> body schema. 204 uses Empty (null). 302 responses are redirects and carry no schema. */
  responses: Readonly<Record<number, TSchema>>;
  /** Error codes this route can answer with, besides the ones every route may return. */
  errors: readonly ErrorCode[];
  /** Free text for the contract document (side effects, cookies, redirects). */
  notes?: string;
}

/** Errors any route may return; not repeated in each route's `errors`. */
export const COMMON_ERRORS = [
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "INTERNAL",
  "NOT_IMPLEMENTED",
] as const satisfies readonly ErrorCode[];

export function route<const R extends RouteSpec>(spec: R): R {
  return spec;
}

/** Adds `id` (the map key) to every route in a module's route map. */
export function withIds<const M extends Record<string, RouteSpec>>(
  map: M,
): { [K in keyof M]: M[K] & { id: K } } {
  return Object.fromEntries(Object.entries(map).map(([id, spec]) => [id, { ...spec, id }])) as never;
}

export type ParamsOf<R> = R extends { params: infer S extends TSchema } ? Static<S> : undefined;
export type QueryOf<R> = R extends { query: infer S extends TSchema } ? Static<S> : undefined;
export type BodyOf<R> = R extends { body: infer S extends TSchema } ? Static<S> : undefined;
export type ResponsesOf<R> = R extends { responses: infer M } ? M : never;
/** Union of the bodies of every 2xx response. 204 is `null`; 302 has no body. */
export type SuccessOf<R> = {
  [S in keyof ResponsesOf<R> & number]: S extends 200 | 201 | 202 | 204
    ? ResponsesOf<R>[S] extends infer U extends TSchema
      ? Static<U>
      : never
    : never;
}[keyof ResponsesOf<R> & number];
/** Body schema of the primary success response (the lowest 2xx status). */
export function successStatuses(spec: RouteSpec): number[] {
  return Object.keys(spec.responses)
    .map(Number)
    .filter((s) => s >= 200 && s < 300)
    .sort((a, b) => a - b);
}
